/**
 * KanbanBoard — the trial board's Kanban layout: the same interleaved trials the
 * {@link ActivityStream} renders as a flat list, grouped instead into the five
 * status lanes (the existing {@link groupByLane} / {@link LANES} lane model).
 *
 * Purely presentational — no fetch, no polling, no global reads. The parent
 * passes an already-scoped/filtered/sorted `TrialLite[]`; this component buckets
 * it by lane (preserving the incoming recency order within each lane) and renders
 * one column per lane with a live count and the per-run colored {@link TrialRow}
 * cards. Each lane owns an honest empty state so an operator can tell an empty
 * lane from a missing one.
 *
 * Lane headers use the operator vocabulary (Waiting / Claimed / Running /
 * Recovering / Done) that the rows already speak, rather than the raw store
 * status names.
 */

import { TrialRow } from './TrialRow'
import { LANES, type LaneId, groupByLane, laneCounts } from './board-lanes'
import type { TrialLite } from './board-types'
import { runColor } from './run-color'
import type { RunStatusCounts } from './types'

/** Operator-vocabulary column headings, keyed to the lane model. */
const LANE_LABELS: Record<LaneId, string> = {
  queued: 'Waiting',
  leased: 'Claimed',
  running: 'Running',
  recovering: 'Recovering',
  done: 'Done',
}

export interface KanbanBoardProps {
  /** Already scoped/filtered/sorted trials from the host-lens snapshot. */
  trials: TrialLite[]
  /** Called with the trialId when a card is clicked — parent wires to the drawer. */
  onSelect: (trialId: string) => void
  /**
   * Authoritative per-lane totals from the snapshot's top-level counts field.
   * When provided, any lane whose loaded card count is less than the authoritative
   * total shows a "showing X of Y" annotation — useful during the transition
   * period where limit=1000 still falls short on very large runs.
   */
  authoritativeCounts?: RunStatusCounts
}

export function KanbanBoard({ trials, onSelect, authoritativeCounts }: KanbanBoardProps): React.JSX.Element {
  const lanes = groupByLane(trials)
  // Authoritative totals from the snapshot counts (full store, not just the loaded page).
  const authTotals = authoritativeCounts ? laneCounts(authoritativeCounts) : null
  return (
    <div className="kanban-board" data-testid="kanban-board" aria-label="Trial board by status">
      {LANES.map((lane) => {
        const laneTrials = lanes[lane.id]
        // Show a truncation hint when we loaded fewer cards than the store total.
        const authTotal = authTotals ? authTotals[lane.id] : null
        const isTruncated = authTotal !== null && laneTrials.length < authTotal
        return (
          <section
            key={lane.id}
            className={`kanban-lane kanban-lane-${lane.id}`}
            data-testid={`kanban-lane-${lane.id}`}
            data-count={laneTrials.length}
          >
            <header className="kanban-lane-header">
              <span className="kanban-lane-label">{LANE_LABELS[lane.id]}</span>
              <span className="kanban-lane-count" data-testid={`kanban-lane-count-${lane.id}`}>
                {isTruncated ? `${laneTrials.length} of ${authTotal}` : laneTrials.length}
              </span>
            </header>
            {laneTrials.length === 0 ? (
              <p className="kanban-lane-empty" data-testid={`kanban-lane-empty-${lane.id}`}>
                —
              </p>
            ) : (
              <ul className="kanban-lane-list">
                {laneTrials.map((trial) => (
                  <TrialRow
                    key={trial.id}
                    trial={trial}
                    color={runColor(trial.runId)}
                    onClick={() => onSelect(trial.id)}
                    mode="kanban"
                  />
                ))}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}
